import { useState } from 'react';
import { AlertCircle, Sparkles } from 'lucide-react';
import { FullscreenShell } from './layout/FullscreenShell';
import { toast } from '../utils/toast';
import { API_BASE } from '../utils/supabase/info';
import { validateText } from '../utils/profanity-filter';

interface ShowPackageRequestModalProps {
  packageId: string;
  packageTitle: string;
  accessToken: string;
  userProfile: any;
  onClose: () => void;
  onSuccess: () => void;
  onNavigateToSubscription?: () => void;
}

export function ShowPackageRequestModal({
  packageId,
  packageTitle,
  accessToken,
  userProfile,
  onClose,
  onSuccess,
  onNavigateToSubscription,
}: ShowPackageRequestModalProps) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error('Vennligst skriv en melding');
      return;
    }
    if (trimmed.length > 200) {
      toast.error('Meldingen kan ikke være lengre enn 200 tegn');
      return;
    }
    const validation = validateText(trimmed, 'Melding');
    if (!validation.isValid) {
      toast.error(validation.error || 'Meldingen inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/packages/${packageId}/interest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: trimmed }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Kunne ikke sende forespørsel');
      }

      toast.success('Forespørsel sendt!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunne ikke sende forespørsel');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FullscreenShell title="Send forespørsel" onClose={onClose} onBack={onClose} containerClassName="xl:max-w-lg" contentClassName="space-y-4">
      <div className="bg-purple-50 p-4 rounded-lg">
        <p className="text-sm text-purple-700 font-medium mb-1">Tjeneste:</p>
        <p className="text-purple-900 font-semibold">{packageTitle}</p>
      </div>

      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-purple-800">
            Alle innloggede brukere kan sende forespørsel på tjenester.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Skriv en kort forespørsel og beskriv hva du trenger hjelp til.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-purple-900 mb-2">Din melding</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="F.eks: Hei! Jeg trenger fotograf til bryllup 12. juni..."
          className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          maxLength={200}
          disabled={submitting}
        />
        <div className="flex justify-end mt-2">
          <span className="text-sm text-gray-600">{message.length}/200</span>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || message.trim().length === 0}
        className={`w-full px-6 py-3 rounded-lg font-semibold transition-all ${
          submitting || message.trim().length === 0
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
        }`}
      >
        {submitting ? 'Sender forespørsel...' : 'Send forespørsel'}
      </button>
    </FullscreenShell>
  );
}
